// Minimal snackbar: one toast at a time, bottom-center, auto-dismiss,
// optional action button ("Undo"). Built for the household auto-learn flow
// but generic — bulk-action Undo can reuse it later.
import { createContext, useContext, useState, useRef, useCallback } from 'react'
import { X } from 'lucide-react'

const ToastContext = createContext(null)

export function useToast() {
  const ctx = useContext(ToastContext)
  // Graceful no-op outside the provider so tests of unrelated components
  // don't have to wrap everything.
  return ctx || { showToast: () => {} }
}

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null) // { message, actionLabel, onAction }
  const timerRef = useRef(null)

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current)
    setToast(null)
  }, [])

  const showToast = useCallback((message, { actionLabel, onAction, duration = 6000 } = {}) => {
    // A new toast replaces the current one — reset its dismiss timer too.
    clearTimeout(timerRef.current)
    setToast({ message, actionLabel, onAction })
    timerRef.current = setTimeout(() => setToast(null), duration)
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div
            role="status"
            className="pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-xl
                       bg-ink-800/95 backdrop-blur-sm border border-ink-600 shadow-2xl
                       shadow-black/40 animate-slide-up max-w-[92vw]"
          >
            <span className="text-xs text-ink-100">{toast.message}</span>
            {toast.actionLabel && (
              <button
                className="text-xs font-semibold text-lime-400 hover:text-lime-300 transition-colors flex-shrink-0"
                onClick={() => { toast.onAction?.(); dismiss() }}
              >
                {toast.actionLabel}
              </button>
            )}
            <button
              className="text-ink-500 hover:text-ink-200 transition-colors flex-shrink-0"
              onClick={dismiss}
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  )
}
